import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LoadingFallback } from "@/components/LoadingFallback";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import { queryConfig } from "@/lib/query-config";
import { conversionTracker } from "@/lib/conversion-tracking";
import { 
  DashboardPageSkeleton, 
  CalendarPageSkeleton, 
  CampaignsPageSkeleton,
  AnalyticsPageSkeleton,
  SettingsPageSkeleton,
  GenericPageSkeleton
} from "@/components/PageSkeletons";

// Initialize conversion tracking
conversionTracker.initialize();

// Lazy load all pages for better performance and code splitting
const Index = lazy(() => import("./pages/Index"));
const Preview = lazy(() => import("./pages/Preview"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Features = lazy(() => import("./pages/Features"));
const Help = lazy(() => import("./pages/Help"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const Auth = lazy(() => import("./pages/Auth"));
const Signup = lazy(() => import("./pages/Signup"));
const Plans = lazy(() => import("./pages/Plans"));
const Billing = lazy(() => import("./pages/Billing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const InviteTeam = lazy(() => import("./pages/InviteTeam"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Analytics = lazy(() => import("./pages/Analytics"));
const ExecutiveDashboard = lazy(() => import("./pages/ExecutiveDashboard"));
const ProspectAlerts = lazy(() => import("./pages/ProspectAlerts"));
const Settings = lazy(() => import("./pages/Settings"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignTemplates = lazy(() => import("./pages/CampaignTemplates"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const CampaignTemplateDetail = lazy(() => import("./pages/CampaignTemplateDetail"));
const CustomCampaignBuilder = lazy(() => import("./pages/CustomCampaignBuilder"));
const Links = lazy(() => import("./pages/Links"));
const Referrals = lazy(() => import("./pages/Referrals"));
const ProductLaunches = lazy(() => import("./pages/ProductLaunches"));
const TrialAttribution = lazy(() => import("./pages/TrialAttribution"));
const EmployeeAdvocacy = lazy(() => import("./pages/EmployeeAdvocacy"));
const Admin = lazy(() => import("./pages/Admin"));
const CTAAnalytics = lazy(() => import("./pages/CTAAnalytics"));
const Insights = lazy(() => import("./pages/Insights"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Phase 5 pages
const Repurpose = lazy(() => import("./pages/Repurpose"));
const MediaLibraryPage = lazy(() => import("./pages/MediaLibrary"));
const SalesRepDashboard = lazy(() => import("./pages/SalesRepDashboard"));
const CompetitorTracking = lazy(() => import("./pages/CompetitorTracking"));
const SalesLeaderboard = lazy(() => import("./pages/SalesLeaderboard"));
const SupportCenter = lazy(() => import("./pages/SupportCenter"));

// Admin pages
const AllRoutes = lazy(() => import("./pages/admin/AllRoutes"));

// Platform pages
const Publishing = lazy(() => import("./pages/platform/Publishing"));
const PlatformAnalytics = lazy(() => import("./pages/platform/Analytics"));
const PlatformCampaigns = lazy(() => import("./pages/platform/Campaigns"));
const Collaboration = lazy(() => import("./pages/platform/Collaboration"));

// Solution pages
const MarketingSaaS = lazy(() => import("./pages/solutions/MarketingSaaS"));
const SalesTechSaaS = lazy(() => import("./pages/solutions/SalesTechSaaS"));
const AdTechSaaS = lazy(() => import("./pages/solutions/AdTechSaaS"));
const CustomerSupportSaaS = lazy(() => import("./pages/solutions/CustomerSupportSaaS"));
const EcommerceSaaS = lazy(() => import("./pages/solutions/EcommerceSaaS"));
const AnalyticsSaaS = lazy(() => import("./pages/solutions/AnalyticsSaaS"));
const FinTechSaaS = lazy(() => import("./pages/solutions/FinTechSaaS"));
const HRTechSaaS = lazy(() => import("./pages/solutions/HRTechSaaS"));
const VerticalSaaS = lazy(() => import("./pages/solutions/VerticalSaaS"));
const MarketingTeams = lazy(() => import("./pages/solutions/MarketingTeams"));
const ProductTeams = lazy(() => import("./pages/solutions/ProductTeams"));
const SalesTeams = lazy(() => import("./pages/solutions/SalesTeams"));
const Founders = lazy(() => import("./pages/solutions/Founders"));
const CustomerSuccess = lazy(() => import("./pages/solutions/CustomerSuccess"));

// Resource pages
const Blog = lazy(() => import("./pages/resources/Blog"));
const BlogPost = lazy(() => import("./pages/resources/BlogPost"));
const Guides = lazy(() => import("./pages/resources/Guides"));

// Company pages
const About = lazy(() => import("./pages/company/About"));
const Customers = lazy(() => import("./pages/company/Customers"));
const Security = lazy(() => import("./pages/Security"));

// Legal pages
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const DataProcessing = lazy(() => import("./pages/legal/DataProcessing"));
const CCPAOptOut = lazy(() => import("./pages/legal/CCPAOptOut"));
const CCPAVerify = lazy(() => import("./pages/legal/CCPAVerify"));

// Tier landing pages
const FreeTier = lazy(() => import("./pages/pricing/FreeTier"));
const StarterTier = lazy(() => import("./pages/pricing/StarterTier"));
const CoreTier = lazy(() => import("./pages/pricing/CoreTier"));
const PipelineTier = lazy(() => import("./pages/pricing/PipelineTier"));
const EnterpriseTier = lazy(() => import("./pages/pricing/EnterpriseTier"));
const DoneForYouTier = lazy(() => import("./pages/pricing/DoneForYouTier"));

const queryClient = new QueryClient(queryConfig);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieConsent />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <AuthProvider>
          <PageErrorBoundary>
            <Suspense fallback={<GenericPageSkeleton />}>
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/preview" element={<Preview />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/features" element={<Features />} />
          <Route path="/help" element={<Help />} />
          <Route path="/help/:categorySlug/:articleSlug" element={<Help />} />
          <Route path="/support" element={<ProtectedRoute><RouteErrorBoundary routeName="Support Center"><Suspense fallback={<GenericPageSkeleton />}><SupportCenter /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
          <Route path="/support/:categorySlug/:articleSlug" element={<ProtectedRoute><RouteErrorBoundary routeName="Support Article"><Suspense fallback={<GenericPageSkeleton />}><SupportCenter /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:id" element={<ResourceDetail />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/email-preferences" element={<EmailPreferences />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/onboarding" element={<ProtectedRoute><RouteErrorBoundary routeName="Onboarding"><Suspense fallback={<GenericPageSkeleton />}><Onboarding /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/accept-invitation" element={<ProtectedRoute><RouteErrorBoundary routeName="Accept Invitation"><Suspense fallback={<GenericPageSkeleton />}><AcceptInvitation /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/invite-team" element={<ProtectedRoute><RouteErrorBoundary routeName="Invite Team"><Suspense fallback={<GenericPageSkeleton />}><InviteTeam /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><RouteErrorBoundary routeName="Dashboard"><Suspense fallback={<DashboardPageSkeleton />}><Dashboard /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/calendar" element={<ProtectedRoute><RouteErrorBoundary routeName="Calendar"><Suspense fallback={<CalendarPageSkeleton />}><Calendar /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/campaigns" element={<ProtectedRoute><RouteErrorBoundary routeName="Campaigns"><Suspense fallback={<CampaignsPageSkeleton />}><Campaigns /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/campaigns/templates" element={<ProtectedRoute><RouteErrorBoundary routeName="Campaign Templates"><Suspense fallback={<CampaignsPageSkeleton />}><CampaignTemplates /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/campaigns/templates/:templateId" element={<ProtectedRoute><RouteErrorBoundary routeName="Campaign Template"><Suspense fallback={<GenericPageSkeleton />}><CampaignTemplateDetail /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/campaigns/custom" element={<ProtectedRoute><RouteErrorBoundary routeName="Custom Campaign"><Suspense fallback={<GenericPageSkeleton />}><CustomCampaignBuilder /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/campaigns/:campaignId" element={<ProtectedRoute><RouteErrorBoundary routeName="Campaign Detail"><Suspense fallback={<GenericPageSkeleton />}><CampaignDetail /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/links" element={<ProtectedRoute><RouteErrorBoundary routeName="Links"><Suspense fallback={<GenericPageSkeleton />}><Links /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/referrals" element={<ProtectedRoute><RouteErrorBoundary routeName="Referrals"><Suspense fallback={<GenericPageSkeleton />}><Referrals /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                {/* A/B Testing - hidden for MVP */}
                {/* <Route path="/ab-tests" element={<ProtectedRoute><RouteErrorBoundary routeName="A/B Tests"><Suspense fallback={<GenericPageSkeleton />}><ABTests /></Suspense></RouteErrorBoundary></ProtectedRoute>} /> */}
                {/* MVP: Multi-brand/product UI hidden - one auto-created brand per org */}
                {/* <Route path="/products" element={<ProtectedRoute><RouteErrorBoundary routeName="Products"><Suspense fallback={<GenericPageSkeleton />}><Products /></Suspense></RouteErrorBoundary></ProtectedRoute>} /> */}
                <Route path="/product-launches" element={<ProtectedRoute><RouteErrorBoundary routeName="Product Launches"><Suspense fallback={<GenericPageSkeleton />}><ProductLaunches /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><RouteErrorBoundary routeName="Analytics"><Suspense fallback={<AnalyticsPageSkeleton />}><Analytics /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/analytics/executive" element={<ProtectedRoute><RouteErrorBoundary routeName="Executive Dashboard"><Suspense fallback={<AnalyticsPageSkeleton />}><ExecutiveDashboard /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/analytics/trials" element={<ProtectedRoute><RouteErrorBoundary routeName="Trial Attribution"><Suspense fallback={<GenericPageSkeleton />}><TrialAttribution /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/analytics/cta" element={<ProtectedRoute><RouteErrorBoundary routeName="CTA Analytics"><Suspense fallback={<GenericPageSkeleton />}><CTAAnalytics /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><RouteErrorBoundary routeName="Settings"><Suspense fallback={<SettingsPageSkeleton />}><Settings /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><RouteErrorBoundary routeName="Billing"><Suspense fallback={<GenericPageSkeleton />}><Billing /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/employee-advocacy" element={<ProtectedRoute><RouteErrorBoundary routeName="Employee Advocacy"><Suspense fallback={<GenericPageSkeleton />}><EmployeeAdvocacy /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><RouteErrorBoundary routeName="Admin"><Suspense fallback={<GenericPageSkeleton />}><Admin /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/admin/all-routes" element={<ProtectedRoute><RouteErrorBoundary routeName="All Routes"><Suspense fallback={<GenericPageSkeleton />}><AllRoutes /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/insights" element={<ProtectedRoute><RouteErrorBoundary routeName="Insights"><Suspense fallback={<GenericPageSkeleton />}><Insights /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/repurpose" element={<ProtectedRoute><RouteErrorBoundary routeName="Repurpose"><Suspense fallback={<GenericPageSkeleton />}><Repurpose /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/media-library" element={<ProtectedRoute><RouteErrorBoundary routeName="Media Library"><Suspense fallback={<GenericPageSkeleton />}><MediaLibraryPage /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/my-performance" element={<ProtectedRoute><RouteErrorBoundary routeName="My Performance"><Suspense fallback={<GenericPageSkeleton />}><SalesRepDashboard /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/sales-leaderboard" element={<ProtectedRoute><RouteErrorBoundary routeName="Sales Leaderboard"><Suspense fallback={<GenericPageSkeleton />}><SalesLeaderboard /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/prospect-alerts" element={<ProtectedRoute><RouteErrorBoundary routeName="Prospect Alerts"><Suspense fallback={<GenericPageSkeleton />}><ProspectAlerts /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/competitor-tracking" element={<ProtectedRoute><RouteErrorBoundary routeName="Competitor Tracking"><Suspense fallback={<GenericPageSkeleton />}><CompetitorTracking /></Suspense></RouteErrorBoundary></ProtectedRoute>} />
                {/* MVP: Team Dashboard hidden for MVP */}
                {/* <Route path="/team-dashboard" element={<ProtectedRoute><RouteErrorBoundary routeName="Team Dashboard"><Suspense fallback={<GenericPageSkeleton />}><TeamDashboard /></Suspense></RouteErrorBoundary></ProtectedRoute>} /> */}
                
                {/* Platform Routes */}
                <Route path="/platform/publishing" element={<Publishing />} />
                <Route path="/platform/analytics" element={<PlatformAnalytics />} />
                <Route path="/platform/campaigns" element={<PlatformCampaigns />} />
                <Route path="/platform/collaboration" element={<Collaboration />} />
                
                {/* Solution Routes */}
                <Route path="/solutions/marketing-saas" element={<MarketingSaaS />} />
                <Route path="/solutions/salestech-saas" element={<SalesTechSaaS />} />
                <Route path="/solutions/adtech-saas" element={<AdTechSaaS />} />
                <Route path="/solutions/customer-support-saas" element={<CustomerSupportSaaS />} />
                <Route path="/solutions/ecommerce-saas" element={<EcommerceSaaS />} />
                <Route path="/solutions/analytics-saas" element={<AnalyticsSaaS />} />
                <Route path="/solutions/fintech-saas" element={<FinTechSaaS />} />
                <Route path="/solutions/hrtech-saas" element={<HRTechSaaS />} />
                <Route path="/solutions/vertical-saas" element={<VerticalSaaS />} />
                <Route path="/solutions/marketing-teams" element={<MarketingTeams />} />
                <Route path="/solutions/product-teams" element={<ProductTeams />} />
                <Route path="/solutions/sales-teams" element={<SalesTeams />} />
                <Route path="/solutions/founders" element={<Founders />} />
                <Route path="/solutions/customer-success" element={<CustomerSuccess />} />
                
                {/* Resource Routes */}
                <Route path="/resources/blog" element={<Blog />} />
                <Route path="/resources/blog/:slug" element={<BlogPost />} />
                <Route path="/resources/guides" element={<Guides />} />
                
                {/* Company Routes */}
                <Route path="/company/about" element={<About />} />
                <Route path="/company/customers" element={<Customers />} />
                <Route path="/security" element={<Security />} />
                
                {/* Legal Routes */}
                <Route path="/legal/terms" element={<Terms />} />
                <Route path="/legal/privacy" element={<Privacy />} />
                <Route path="/legal/data-processing" element={<DataProcessing />} />
                <Route path="/legal/ccpa-opt-out" element={<CCPAOptOut />} />
                <Route path="/legal/ccpa-verify" element={<CCPAVerify />} />
                
                {/* Tier Landing Pages */}
                <Route path="/pricing/free" element={<FreeTier />} />
                <Route path="/pricing/starter" element={<StarterTier />} />
                <Route path="/pricing/core" element={<CoreTier />} />
                <Route path="/pricing/pipeline" element={<PipelineTier />} />
                <Route path="/pricing/enterprise" element={<EnterpriseTier />} />
                <Route path="/pricing/done-for-you" element={<DoneForYouTier />} />
                
                {/* Honeypot Routes - Redirect to 404 */}
                <Route path="/admin-panel" element={<NotFound />} />
                <Route path="/api-keys" element={<NotFound />} />
                <Route path="/database-backup" element={<NotFound />} />
                <Route path="/internal-docs" element={<NotFound />} />
                <Route path="/scraper-trap" element={<NotFound />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PageErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
